import { useMemo, useState, useEffect } from 'react';
import {
  ArrowDown,
  ArrowRight,
  BookmarkCheck,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ClipboardList,
  FileText,
  Filter,
  Folder,
  HelpCircle,
  MapPin,
  MessageSquare,
  MoreVertical,
  PenLine,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { resolveStorageImageUrl } from '../../../lib/imageUrl';
import { formatCurrency } from '../../data/ownerPrototype';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import type { OwnerProjectDto, OwnerProjectStatus } from '../../../api/manpro';

type MainTabType = 'tender' | 'manpro';

type ProjectRowStatus = 'Aktif' | 'Dalam Proses' | 'Monitoring' | 'Selesai' | 'Dihentikan';

type ProjectRow = {
  id: string;
  title: string;
  location: string;
  category: string;
  budget: string;
  area: string;
  images: string[];
  vendorCount: number;
  extraVendorCount: number;
  deadlineDate: string;
  deadlineNote: string;
  status: ProjectRowStatus;
  createdLabel: string;
  progress: number;
  stageName: string;
  sourceProject?: OwnerProjectDto;
};

type InfoListItem = {
  title: string;
  description: string;
  meta?: string;
  icon: LucideIcon;
};

const projectImages = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&q=80&w=500',
];

const activityItems: InfoListItem[] = [
  {
    title: '7 penawaran baru',
    description: 'Renovasi Rumah Type 90',
    meta: '2 jam yang lalu',
    icon: FileText,
  },
  {
    title: '3 penawaran baru',
    description: 'Pembangunan Gudang 500 m²',
    meta: '5 jam yang lalu',
    icon: ClipboardList,
  },
  {
    title: 'Vendor mengajukan pertanyaan',
    description: 'Desain Interior Kantor',
    meta: '1 hari yang lalu',
    icon: MessageSquare,
  },
];

const tipItems: InfoListItem[] = [
  {
    title: 'Lengkapi informasi proyek',
    description: 'Proyek dengan informasi lengkap mendapat lebih banyak penawaran',
    icon: ShieldCheck,
  },
  {
    title: 'Upload foto & dokumen',
    description: 'Foto dan dokumen membantu vendor memahami kebutuhan Anda',
    icon: FileText,
  },
  {
    title: 'Tentukan budget yang realistis',
    description: 'Budget yang jelas mempercepat proses penawaran',
    icon: CheckCircle2,
  },
];

const statusTone: Record<ProjectRowStatus, string> = {
  Aktif: 'bg-emerald-50 text-emerald-700',
  'Dalam Proses': 'bg-amber-50 text-amber-700',
  Monitoring: 'bg-blue-50 text-blue-700',
  Selesai: 'bg-emerald-50 text-emerald-700',
  Dihentikan: 'bg-slate-100 text-slate-500',
};

const getPublishStateLabel = (project?: OwnerProjectDto) => {
  if (!project) return 'Prototype';
  if (project.status === 'draft') return 'Draft';
  if (project.status === 'open_for_bid' || project.status === 'proposal_review') return 'Terbuka';
  if (project.status === 'accepted') return 'Vendor Terpilih';
  if (project.status === 'converted_to_manpro') return 'Monitoring MANPRO';
  if (project.status === 'completed') return 'Selesai';
  if (project.status === 'cancelled') return 'Dibatalkan';
  return 'Terbuka';
};

const getPublishStateKey = (project?: OwnerProjectDto) => {
  if (!project) return 'draft';
  if (project.status === 'draft') return 'draft';
  if (project.status === 'accepted' || project.status === 'converted_to_manpro') return 'monitoring';
  if (project.status === 'completed') return 'closed';
  if (project.status === 'cancelled') return 'cancelled';
  return 'published';
};

const mapStatusToRowStatus = (status: OwnerProjectStatus): ProjectRowStatus => {
  if (status === 'completed') return 'Selesai';
  if (status === 'accepted' || status === 'converted_to_manpro') return 'Monitoring';
  if (status === 'proposal_review') return 'Dalam Proses';
  if (status === 'cancelled') return 'Dihentikan';
  return 'Aktif';
};

const getOwnerProjectRowStatus = (project: OwnerProjectDto): ProjectRowStatus => {
  if (project.status === 'completed') return 'Selesai';
  if (project.status === 'cancelled') return 'Dihentikan';

  const hasAcceptedProvider = Boolean(
    project.accepted_post_bid_id ||
    project.manpro_project_id ||
    project.bids?.some((bid) => bid.status === 'accepted'),
  );

  if (hasAcceptedProvider) return 'Monitoring';
  return mapStatusToRowStatus(project.status);
};

const getProjectDate = (date: string) =>
  new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(date));

const getProjectBudgetLabel = (project: OwnerProjectDto) => {
  if (project.budget_range) return project.budget_range;
  const budget = project.budget_max ?? project.budget_min ?? 0;
  return budget > 0 ? formatCurrency(budget) : 'Belum diisi';
};

const getDeadlineNote = (project: OwnerProjectDto) => {
  if (project.status === 'completed') return '';
  if (!project.bid_deadline) return '-';

  const deadline = new Date(project.bid_deadline);
  const diffDays = Math.ceil((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  if (Number.isNaN(diffDays)) return '-';
  if (diffDays < 0) return 'Lewat deadline';
  if (diffDays === 0) return 'Hari ini';
  return `${diffDays} hari lagi`;
};

const getProjectRows = (projects: OwnerProjectDto[]): ProjectRow[] => {
  return projects.map((project, index) => {
    const rawStatus = project.status;
    let progress = typeof project.progress_percent === 'number' ? project.progress_percent : 0;
    let stageName = 'Persiapan Eksekusi';


    if (rawStatus === 'completed') {
      progress = 100;
      stageName = 'Pekerjaan Selesai';
    } else if (rawStatus === 'converted_to_manpro' || rawStatus === 'in_delivery') {
      if (typeof project.progress_percent !== 'number') progress = 60; // only fallback if api doesn't give a number
      stageName = 'Pekerjaan Struktur';
    } else if (rawStatus === 'accepted') {
      if (typeof project.progress_percent !== 'number') progress = 20;
      stageName = 'Persiapan Eksekusi';
    }

    return {
      id: project.id,
      title: project.title,
      location: project.location || project.city || '-',
      category: project.category || '-',
      budget: getProjectBudgetLabel(project),
      area: project.requirements?.find((item) => item.includes('m²')) ?? '-',
      images: (project.documents?.filter((doc) => doc.document_category === 'location_photo') || []).length > 0
        ? project.documents!.filter((doc) => doc.document_category === 'location_photo').map(doc => resolveStorageImageUrl(doc.file_path))
        : [projectImages[index % projectImages.length]],
      vendorCount: project.bids_count ?? project.bids?.length ?? 0,
      extraVendorCount: Math.max((project.bids_count ?? project.bids?.length ?? 0) - 3, 0),
      deadlineDate: project.bid_deadline ? getProjectDate(project.bid_deadline) : '-',
      deadlineNote: getDeadlineNote(project),
      status: getOwnerProjectRowStatus(project),
      createdLabel: project.created_at ? getProjectDate(project.created_at) : '-',
      progress,
      stageName,
      sourceProject: project,
    };
  });
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const ProjectImageSlider = ({ images, title, isDraft }: { images: string[]; title: string; isDraft: boolean }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  return (
    <img
      src={images[currentIndex]}
      alt={title}
      className={`h-full w-full object-cover transition-transform duration-300 hover:scale-105 ${isDraft ? 'grayscale opacity-60' : ''}`}
    />
  );
};

const ProyekSaya = () => {
  const { projects, projectPagination, projectParams, setProjectParams, isLoadingProjects, deleteProject, isDeletingProject } = useOwnerManpro();
  const [mainTab, setMainTab] = useState<MainTabType>('tender');
  const [subFilter, setSubFilter] = useState<string>('Semua');
  const [query, setQuery] = useState('');
  const [openActionProjectId, setOpenActionProjectId] = useState<string | null>(null);

  const projectRows = useMemo(() => getProjectRows(projects), [projects]);

  const tenderProjects = useMemo(
    () =>
      projectRows.filter((p) => {
        const rawStatus = p.sourceProject?.status;
        return !rawStatus || ['draft', 'open_for_bid', 'proposal_review', 'accepted'].includes(rawStatus) || p.status === 'Aktif' || p.status === 'Dalam Proses';
      }),
    [projectRows],
  );

  const manproProjects = useMemo(
    () =>
      projectRows.filter((p) => {
        const rawStatus = p.sourceProject?.status;
        return (
          rawStatus === 'converted_to_manpro' ||
          rawStatus === 'completed' ||
          rawStatus === 'cancelled' ||
          p.status === 'Monitoring' ||
          p.status === 'Selesai' ||
          p.status === 'Dihentikan'
        );
      }),
    [projectRows],
  );

  const currentTabProjects = mainTab === 'tender' ? tenderProjects : manproProjects;

  const subFilterOptions = useMemo(() => {
    if (mainTab === 'tender') {
      return ['Semua', 'Aktif', 'Dalam Proses', 'Vendor Terpilih'];
    }
    return ['Semua', 'Monitoring', 'Selesai', 'Dihentikan'];
  }, [mainTab]);

  const filteredRows = useMemo(
    () =>
      currentTabProjects.filter((project) => {
        const matchesSubFilter = subFilter === 'Semua' || project.status === subFilter || (subFilter === 'Vendor Terpilih' && project.status === 'Monitoring');
        const matchesQuery = `${project.title} ${project.location} ${project.category}`
          .toLowerCase()
          .includes(query.toLowerCase());

        return matchesSubFilter && matchesQuery;
      }),
    [currentTabProjects, subFilter, query],
  );

  const stats = useMemo(
    () => [
      {
        label: 'Tender Pra-Konstruksi',
        value: tenderProjects.length,
        caption: 'Proyek membuka bid',
        icon: Folder,
        tone: 'bg-blue-50 text-blue-700',
      },
      {
        label: 'Proyek Berjalan (MANPRO)',
        value: manproProjects.filter((p) => {
          const rawStatus = p.sourceProject?.status;
          return rawStatus === 'accepted' || rawStatus === 'converted_to_manpro' || rawStatus === 'in_delivery';
        }).length,
        caption: 'Eksekusi & monitoring',
        icon: Briefcase,
        tone: 'bg-indigo-50 text-indigo-700',
      },
      {
        label: 'Total Penawaran Masuk',
        value: projectRows.reduce((total, project) => total + project.vendorCount, 0),
        caption: 'Proposal dari vendor',
        icon: ClipboardList,
        tone: 'bg-emerald-50 text-emerald-700',
      },
      {
        label: 'Proyek Selesai',
        value: projectRows.filter((p) => p.sourceProject?.status === 'completed').length,
        caption: 'Serah terima rampung',
        icon: Check,
        tone: 'bg-purple-50 text-purple-700',
      },
    ],
    [tenderProjects.length, manproProjects, projectRows],
  );

  const stopProject = async (project: ProjectRow) => {
    if (!project.sourceProject) {
      toast('Project prototype belum terhubung ke backend.');
      return;
    }

    if (project.sourceProject.status !== 'draft') {
      toast('Project yang sudah publish tidak bisa dihentikan dari halaman Owner.');
      return;
    }

    await deleteProject(project.sourceProject.id);
  };

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Proyek Saya</h1>
          <p className="mt-1 text-sm text-slate-500">Kelola tender pra-konstruksi dan pantau eksekusi proyek di MANPRO.</p>
        </div>
        <Link
          to="/owner/pasang"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700"
        >
          <Folder size={18} />
          Pasang Proyek Baru
        </Link>
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
  <div className="min-w-0 space-y-4 sm:space-y-5">
    {/* Stats: 2 Kolom di Mobile, 4 Kolom di Desktop */}
    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
      {stats.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.label}
            className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-[11px] sm:text-xs font-extrabold text-slate-500">
                  {item.label}
                </p>
                <p className="mt-1 sm:mt-2 truncate text-lg sm:text-2xl font-extrabold text-slate-900">
                  {item.value}
                </p>
                <p className="mt-0.5 truncate text-[10px] sm:text-xs text-slate-400">
                  {item.caption}
                </p>
              </div>
              <div
                className={`flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl ${item.tone}`}
              >
                <Icon size={18} className="sm:h-5 sm:w-5" />
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Main Tab Bar: 2 Kolom Kiri & Kanan di Mobile & Desktop */}
    <div className="rounded-2xl border border-slate-200 bg-slate-100/80 p-1 sm:p-1.5 shadow-inner">
      <div className="grid grid-cols-2 gap-1 sm:gap-2">
        <button
          type="button"
          onClick={() => {
            setMainTab('tender');
            setSubFilter('Semua');
          }}
          className={`flex items-center justify-between sm:justify-center gap-1.5 sm:gap-2 rounded-xl px-2.5 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-extrabold transition-all ${
            mainTab === 'tender'
              ? 'bg-white text-blue-700 shadow-md ring-1 ring-slate-200'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
          }`}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <ClipboardList
              size={16}
              className={`shrink-0 sm:h-4.5 sm:w-4.5 ${
                mainTab === 'tender' ? 'text-blue-600' : 'text-slate-400'
              }`}
            />
            <span className="truncate text-left">
              1. Penawaran <span className="hidden sm:inline">&amp; Tender (Pra-Konstruksi)</span>
            </span>
          </div>
          <span
            className={`shrink-0 rounded-full px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-bold ${
              mainTab === 'tender'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-slate-200 text-slate-600'
            }`}
          >
            {tenderProjects.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => {
            setMainTab('manpro');
            setSubFilter('Semua');
          }}
          className={`flex items-center justify-between sm:justify-center gap-1.5 sm:gap-2 rounded-xl px-2.5 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-extrabold transition-all ${
            mainTab === 'manpro'
              ? 'bg-white text-indigo-700 shadow-md ring-1 ring-slate-200'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
          }`}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <Briefcase
              size={16}
              className={`shrink-0 sm:h-4.5 sm:w-4.5 ${
                mainTab === 'manpro' ? 'text-indigo-600' : 'text-slate-400'
              }`}
            />
            <span className="truncate text-left">
              2. Proyek Berjalan <span className="hidden sm:inline">(Eksekusi MANPRO)</span>
            </span>
          </div>
          <span
            className={`shrink-0 rounded-full px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-bold ${
              mainTab === 'manpro'
                ? 'bg-indigo-100 text-indigo-800'
                : 'bg-slate-200 text-slate-600'
            }`}
          >
            {manproProjects.length}
          </span>
        </button>
      </div>
    </div>

    {/* Sub Filter & Search Bar */}
    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {subFilterOptions.map((filter) => (
          <button
            key={filter}
            onClick={() => setSubFilter(filter)}
            className={`shrink-0 rounded-xl px-3.5 sm:px-4 py-2 text-xs font-extrabold transition-all active:scale-95 ${
              subFilter === filter
                ? 'bg-slate-900 text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
            type="button"
          >
            {filter}
          </button>
        ))}
      </div>

      <div className="w-full sm:w-64">
        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-100">
          <Search size={15} className="shrink-0 text-slate-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full bg-transparent text-xs text-slate-700 outline-none placeholder:text-slate-400"
            placeholder={
              mainTab === 'tender' ? 'Cari tender...' : 'Cari proyek berjalan...'
            }
          />
        </label>
      </div>
    </div>

    {/* Project List */}
    <div className="space-y-3 sm:space-y-4">
      {filteredRows.map((project) => {
        const canManageDraft = project.sourceProject?.status === 'draft';
        const isManproMode = mainTab === 'manpro';
        const isDraft = project.sourceProject?.status === 'draft';

        return (
          <article
            key={project.id}
            className={`relative rounded-2xl border transition-all shadow-sm ${
              isDraft
                ? 'border-dashed border-amber-300 bg-amber-50/40 hover:border-amber-400 hover:shadow-md'
                : 'border-slate-200 bg-white hover:border-blue-200 hover:shadow-md'
            }`}
          >
            {/* Draft top banner */}
            {isDraft && (
              <div className="flex items-center gap-2 rounded-t-2xl border-b border-dashed border-amber-200 bg-amber-100/70 px-3.5 py-2">
                <PenLine size={13} className="shrink-0 text-amber-600" />
                <p className="text-[11px] font-extrabold tracking-wide text-amber-700">
                  DRAFT — Belum Dipublikasikan
                </p>
                <span className="ml-auto text-[10px] font-semibold text-amber-600">
                  Selesaikan &amp; publish agar vendor dapat melihat proyek ini
                </span>
              </div>
            )}

            <div className="p-3.5 sm:p-4">
            <div className="flex flex-col gap-3.5 sm:gap-4 lg:grid lg:grid-cols-[130px_minmax(200px,1fr)_150px_140px_130px] lg:items-center">
              {/* Image & Title Header */}
              <div className="flex gap-3 lg:contents">
                <div className="relative h-20 w-24 sm:h-24 sm:w-32 lg:h-28 lg:w-full shrink-0 overflow-hidden rounded-xl bg-slate-100">
                  <ProjectImageSlider images={project.images} title={project.title} isDraft={isDraft} />
                  {/* Draft ribbon overlay */}
                  {isDraft ? (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="rotate-[-25deg] rounded-md bg-amber-500/90 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-white shadow-sm">
                        Draft
                      </span>
                    </div>
                  ) : (
                    <span className="absolute bottom-1 left-1 lg:bottom-2 lg:left-2 rounded-md bg-slate-900/80 px-1.5 py-0.5 text-[9px] sm:text-[10px] font-extrabold text-white backdrop-blur-sm pointer-events-none">
                      {project.category}
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <h2 className={`truncate text-sm sm:text-base font-extrabold ${isDraft ? 'text-amber-900' : 'text-slate-900'}`}>
                      {project.title}
                    </h2>
                    {!isDraft && (
                      <span
                        className={`shrink-0 rounded-full px-2 sm:px-2.5 py-0.5 text-[10px] sm:text-[11px] font-extrabold ${
                          statusTone[project.status]
                        }`}
                      >
                        {project.status === 'Monitoring' ? 'Live MANPRO' : project.status}
                      </span>
                    )}
                    {/* Live published dot */}
                    {!isDraft && project.status === 'Aktif' && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                        Live
                      </span>
                    )}
                  </div>

                  <div className="mt-1.5 sm:mt-2 space-y-0.5 sm:space-y-1 text-[11px] sm:text-xs font-medium text-slate-500">
                    <p className="flex items-center gap-1.5 truncate">
                      <MapPin size={13} className="shrink-0 text-slate-400" />
                      <span>{project.location}</span>
                    </p>
                    <p className="flex items-center gap-1.5 truncate">
                      <Calendar size={13} className="shrink-0 text-slate-400" />
                      <span>Dibuat: {project.createdLabel}</span>
                    </p>
                  </div>

                  <div className="mt-2.5 sm:mt-3 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs font-extrabold text-slate-800">
                    <span className={`rounded-lg px-2 py-0.5 sm:px-2.5 sm:py-1 ${isDraft ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-blue-700'}`}>
                      {project.budget}
                    </span>
                    {project.area !== '-' && (
                      <span className="text-slate-500">Luas: {project.area}</span>
                    )}
                    {isDraft && (
                      <span className="text-amber-600/80">{project.category || '-'}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status / Detail: 2 Kolom di Mobile */}
              <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 lg:contents lg:border-0 lg:pt-0">
                {!isManproMode ? (
                  <div className="lg:border-l lg:pl-4">
                    <p className="text-[11px] sm:text-xs font-extrabold text-slate-800">
                      Penawaran Masuk
                    </p>
                    {isDraft ? (
                      <p className="mt-1 text-[11px] sm:text-xs text-amber-600 font-semibold italic">
                        Belum bisa menerima penawaran
                      </p>
                    ) : (
                      <>
                        <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-bold text-blue-600">
                          {project.vendorCount} Proposal
                        </p>
                        <div className="mt-1.5 sm:mt-2 flex items-center flex-wrap">
                          {project.sourceProject?.bids?.slice(0, 3).map((bid, index) => {
                            const companyName = bid.provider?.company_name || 'Vendor';
                            return (
                              <div
                                key={bid.id || index}
                                title={companyName}
                                className="-ml-1.5 first:ml-0 flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full border-2 border-white bg-blue-100 text-[9px] sm:text-[10px] font-extrabold text-blue-700 shrink-0"
                              >
                                {getInitials(companyName)}
                              </div>
                            );
                          })}
                          {project.extraVendorCount > 0 && (
                            <span className="-ml-1.5 flex h-6 min-w-6 sm:h-7 sm:min-w-7 items-center justify-center rounded-full border-2 border-white bg-slate-100 px-1 text-[9px] sm:text-[10px] font-extrabold text-slate-700 shrink-0">
                              +{project.extraVendorCount}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="lg:border-l lg:pl-4">
                    <p className="text-[11px] sm:text-xs font-extrabold text-slate-800">
                      Progress Eksekusi
                    </p>
                    <div className="mt-1.5 sm:mt-2 flex items-center gap-2">
                      <div className="h-1.5 sm:h-2 w-full flex-1 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-600 transition-all duration-500"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-extrabold text-indigo-700">
                        {project.progress}%
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] sm:text-[11px] font-bold text-slate-500 truncate">
                      Tahap: {project.stageName}
                    </p>
                  </div>
                )}

                {!isManproMode ? (
                  <div className="border-l border-slate-100 pl-3 lg:pl-4">
                    <p className="text-[11px] sm:text-xs font-extrabold text-slate-800">
                      Batas Penawaran
                    </p>
                    {isDraft ? (
                      <p className="mt-1 text-[11px] sm:text-xs text-amber-600 font-semibold italic">
                        Atur setelah publish
                      </p>
                    ) : (
                      <>
                        <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-slate-600">
                          {project.deadlineDate}
                        </p>
                        {project.deadlineNote && (
                          <span className="mt-1 inline-block rounded-md bg-amber-50 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-extrabold text-amber-700 truncate max-w-full">
                            {project.deadlineNote}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="border-l border-slate-100 pl-3 lg:pl-4">
                    <p className="text-[11px] sm:text-xs font-extrabold text-slate-800">
                      Kontraktor
                    </p>
                    <p className="mt-0.5 sm:mt-1 truncate text-[11px] sm:text-xs font-bold text-slate-900">
                      {project.sourceProject?.accepted_post_bid?.provider
                        ?.company_name || 'PT Tekno Karya Interior'}
                    </p>
                    <span className="mt-1 inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold text-emerald-700">
                      <CheckCircle2 size={12} className="shrink-0" /> Disetujui
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 border-t border-slate-100 pt-3 lg:border-t-0 lg:pt-0">
                {isDraft ? (
                  <Link
                    to={`/owner/pasang/${project.id}/edit`}
                    className="flex-1 rounded-xl bg-amber-500 px-3 py-2.5 text-center text-xs font-extrabold text-white shadow-sm shadow-amber-100 transition-all hover:bg-amber-600 active:scale-95"
                  >
                    Selesaikan &amp; Publish →
                  </Link>
                ) : (
                  <Link
                    to={`/owner/proyek-saya/${project.id}`}
                    className={`flex-1 rounded-xl px-3 py-2.5 text-center text-xs font-extrabold text-white transition-all shadow-sm active:scale-95 ${
                      isManproMode
                        ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100'
                        : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                    }`}
                  >
                    {isManproMode ? 'Live Tracking' : 'Review Proposal'}
                  </Link>
                )}

                {canManageDraft && (
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenActionProjectId((current) =>
                          current === project.id ? null : project.id
                        )
                      }
                      className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 active:scale-95"
                      aria-label="Menu proyek"
                      type="button"
                    >
                      <MoreVertical size={18} />
                    </button>

                    {openActionProjectId === project.id && (
                      <div className="absolute right-0 z-20 mt-2 w-36 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 text-xs shadow-xl">
                        <Link
                          to={`/owner/pasang/${project.id}/edit`}
                          onClick={() => setOpenActionProjectId(null)}
                          className="block px-4 py-2 font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                        >
                          Edit Draft
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenActionProjectId(null);
                            void stopProject(project);
                          }}
                          disabled={isDeletingProject}
                          className="block w-full px-4 py-2 text-left font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Hapus Draft
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </div>
          </article>
        );
      })}


      {filteredRows.length === 0 && (
        <div className="rounded-2xl sm:rounded-3xl border border-dashed border-slate-300 bg-white p-8 sm:p-12 text-center">
          <div className="mx-auto flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            {mainTab === 'tender' ? (
              <ClipboardList size={26} className="sm:h-8 sm:w-8" />
            ) : (
              <Briefcase size={26} className="sm:h-8 sm:w-8" />
            )}
          </div>
          <h3 className="mt-4 text-base sm:text-lg font-extrabold text-slate-900">
            {mainTab === 'tender'
              ? 'Belum Ada Proyek Tender'
              : 'Belum Ada Proyek Berjalan di MANPRO'}
          </h3>
          <p className="mx-auto mt-2 max-w-md text-xs sm:text-sm leading-relaxed text-slate-500">
            {mainTab === 'tender'
              ? 'Pasang proyek baru untuk mulai menerima penawaran harga dari kontraktor terverifikasi Indoteknisi.'
              : 'Proyek yang penawarannya sudah disetujui akan otomatis masuk ke tahap eksekusi MANPRO di sini.'}
          </p>
          {mainTab === 'tender' && (
            <Link
              to="/owner/pasang"
              className="mt-5 sm:mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 sm:py-3 text-xs sm:text-sm font-extrabold text-white shadow-lg shadow-blue-100 hover:bg-blue-700 active:scale-95"
            >
              <Folder size={16} />
              <span>Pasang Proyek Sekarang</span>
            </Link>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {projectPagination && projectPagination.last_page > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-500">
            Menampilkan <span className="font-bold">{projectPagination.from || 0}</span> - <span className="font-bold">{projectPagination.to || 0}</span> dari <span className="font-bold">{projectPagination.total}</span> proyek
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setProjectParams({ ...projectParams, offset: Math.max(1, (projectParams.offset || 1) - 1) })}
              disabled={(projectParams.offset || 1) <= 1}
              className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Sebelumnya
            </button>
            <div className="flex items-center px-2 text-sm font-bold text-slate-600">
              Halaman {projectPagination.current_page} / {projectPagination.last_page}
            </div>
            <button
              onClick={() => setProjectParams({ ...projectParams, offset: (projectParams.offset || 1) + 1 })}
              disabled={(projectParams.offset || 1) >= projectPagination.last_page}
              className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Selanjutnya
            </button>
          </div>
        </div>
      )}
    </div>
  </div>

  {/* Sidebar: 2 Kolom di Layar Tablet (md), 1 Kolom di Desktop/Mobile */}
  <aside className="w-full space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0 lg:flex lg:flex-col lg:space-y-4 lg:sticky lg:top-24 lg:w-[300px]">
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
      <h2 className="text-sm sm:text-base font-extrabold text-slate-900">
        Ringkasan Aktivitas
      </h2>
      <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
        {activityItems.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} className="flex gap-3">
              <div className="flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <Icon size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs font-extrabold text-slate-800">
                  {item.title}
                </p>
                <p className="mt-0.5 truncate text-[11px] font-medium text-slate-500">
                  {item.description}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">{item.meta}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
      <h2 className="text-sm sm:text-base font-extrabold text-slate-900">
        Tips Sukses Owner
      </h2>
      <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
        {tipItems.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} className="flex gap-3">
              <div className="flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Icon size={15} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-slate-800">{item.title}</p>
                <p className="mt-0.5 text-[11px] leading-4 text-slate-500">
                  {item.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </aside>
</section>
    </div>
  );
};

export default ProyekSaya;
